import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Icon } from 'components/ui/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { cn } from 'lib/utils';
import { EMAIL_VALIDATION, PHONE_NUMBER_VALIDATION } from 'utils/validation';
import ErrorMessage from 'components/errorMessage';
import InfoPopover from 'components/info-popover';
import { Controller, useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { IVirtualTerminalForm } from './payment-collection';
import { VIRTUAL_TERMINAL } from 'state/virtualTerminal/type';

interface PaymentInfoFormProps {
  defaultEmail?: string;
  defaultPhone?: string;
  submitToSDK?: boolean;
  isDrawerOpen: boolean;
  paymentData?: IVirtualTerminalForm | null;
  isUnpaidTicketFlow?: boolean;
  locations?: VIRTUAL_TERMINAL.ILocation[];
  locationSelected?: VIRTUAL_TERMINAL.ILocation;
  drawers?: VIRTUAL_TERMINAL.IDrawer[];
  drawerSelected?: VIRTUAL_TERMINAL.IDrawer;
  isSellTreezEntity?: boolean;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  onLocationSelect: (data: VIRTUAL_TERMINAL.ILocation) => void;
  onDrawerSelect: (data: VIRTUAL_TERMINAL.IDrawer) => void;
  onSubmit: (data: IVirtualTerminalForm) => void;
}

const fieldLabelBase = 'text-sm font-medium text-gray-blue-80';
const fieldLabel = cn(fieldLabelBase, 'mb-1 block');
const dropdownTriggerClass =
  'w-full inline-flex items-center justify-between px-3 h-10 rounded-md border border-gray-blue-20 bg-white text-sm text-gray-blue-90 hover:bg-gray-blue-2 disabled:opacity-50 disabled:cursor-not-allowed';

const PaymentInfoForm = ({
  defaultEmail,
  defaultPhone,
  submitToSDK,
  isDrawerOpen,
  paymentData,
  isUnpaidTicketFlow,
  locations,
  locationSelected,
  drawers,
  drawerSelected,
  isSellTreezEntity,
  setIsDrawerOpen,
  onLocationSelect,
  onDrawerSelect,
  onSubmit,
}: PaymentInfoFormProps): JSX.Element => {
  const closeForm = () => setIsDrawerOpen(!isDrawerOpen);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<IVirtualTerminalForm>();

  useEffect(() => {
    if (paymentData) {
      setValue('email', paymentData.email || '');
      setValue('phone', paymentData.phone || '');
      setValue('ticketAlphaNumId', paymentData.ticketAlphaNumId || '');
      setValue('firstName', paymentData.firstName || '');
      setValue('lastName', paymentData.lastName || '');
    } else if (defaultEmail) {
      setValue('email', defaultEmail);
    } else if (defaultPhone) {
      setValue('phone', defaultPhone);
    }
  }, [paymentData, defaultEmail, defaultPhone, setValue]);

  const onFormSubmit = (data: IVirtualTerminalForm) => {
    const mergedData = {
      ...(paymentData || {}),
      ...data,
    };
    onSubmit(mergedData);
    setIsDrawerOpen(false);
  };

  const sortedDrawers = useMemo(() => {
    if (!drawers) return [];
    return [...drawers].sort((a, b) => {
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [drawers]);

  const drawerDisabled = !isUnpaidTicketFlow;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(onFormSubmit)(e);
      }}
      className="space-y-3"
    >
      <div>
        <Label htmlFor="location" className={fieldLabel}>
          Location
        </Label>
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="provider_dropdown"
            id="location"
            className={dropdownTriggerClass}
          >
            <span className="truncate">
              {locationSelected?.name || 'Select location*'}
            </span>
            <Icon name="chevron-down" size={16} aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
            {locations?.map((item) => (
              <DropdownMenuItem
                key={item.id}
                onClick={() => onLocationSelect(item)}
              >
                {item?.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isSellTreezEntity && (
        <div>
          <Label htmlFor="drawer" className={fieldLabel}>
            Drawer
          </Label>
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="drawer_dropdown"
              id="drawer"
              disabled={drawerDisabled}
              className={dropdownTriggerClass}
            >
              <span className="flex items-center justify-between flex-grow mr-2">
                <span className="truncate">
                  {drawerSelected
                    ? drawerSelected.name
                    : drawers?.length === 0
                    ? 'None'
                    : 'Select drawer'}
                </span>
                {drawerSelected?.isActive && (
                  <Badge
                    variant="default"
                    className="ml-2 bg-green-500 text-white"
                  >
                    Active
                  </Badge>
                )}
              </span>
              <Icon name="chevron-down" size={16} aria-hidden="true" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
              {sortedDrawers.map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  onClick={() => onDrawerSelect(item)}
                  className="flex items-center justify-between"
                >
                  <span>{item.name}</span>
                  {item.isActive && (
                    <Badge
                      variant="default"
                      className="ml-2 bg-green-500 text-white"
                    >
                      Active
                    </Badge>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div>
        <Label htmlFor="firstName" className={fieldLabel}>
          First Name
        </Label>
        <Input
          id="firstName"
          placeholder="First Name"
          disabled={isUnpaidTicketFlow}
          {...register('firstName')}
        />
      </div>

      <div>
        <Label htmlFor="lastName" className={fieldLabel}>
          Last Name
        </Label>
        <Input
          id="lastName"
          placeholder="Last Name"
          disabled={isUnpaidTicketFlow}
          {...register('lastName')}
        />
      </div>

      <div>
        <Label htmlFor="email" className={fieldLabel}>
          Email
        </Label>
        <Input
          id="email"
          placeholder="abc@workemail.com"
          {...register('email', {
            pattern: {
              value: EMAIL_VALIDATION,
              message: 'Please enter a valid email',
            },
          })}
        />
        {errors?.email && (
          <ErrorMessage isValidation message="Please enter a valid email" />
        )}
      </div>

      <div>
        <Label htmlFor="phone" className={fieldLabel}>
          Phone number
        </Label>
        <Controller
          control={control}
          name="phone"
          rules={{
            pattern: {
              value: PHONE_NUMBER_VALIDATION,
              message: 'Please enter a valid phone number',
            },
          }}
          defaultValue=""
          render={({ field }) => (
            <NumberFormat
              format="###-###-####"
              id="phone"
              name="phone"
              mask="_"
              placeholder="555-555-5555"
              className={cn(
                'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:text-sm',
                errors.phone && 'border-red-400'
              )}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                field.onChange(e)
              }
              value={field.value}
              getInputRef={field.ref}
            />
          )}
        />
        {errors?.phone && (
          <ErrorMessage
            isValidation
            message="Please enter a valid phone number"
          />
        )}
      </div>

      <div>
        <div className="flex items-center gap-1 mb-1">
          <Label htmlFor="ticketAlphaNumId" className={fieldLabelBase}>
            Reference ID
          </Label>
          <InfoPopover
            label="More information about Reference ID"
            data-testid="reference-id-info"
          >
            <p>Enter the order number being paid for.</p>
            <p>
              Note: Treez POS users should always start payments from the Unpaid
              Tickets section.
            </p>
          </InfoPopover>
        </div>
        <Input
          id="ticketAlphaNumId"
          placeholder="Reference ID"
          disabled={isUnpaidTicketFlow}
          readOnly={isUnpaidTicketFlow}
          {...register('ticketAlphaNumId', {
            maxLength: {
              value: 48,
              message: 'Reference ID must be 48 characters or less',
            },
          })}
        />
        {errors?.ticketAlphaNumId && (
          <ErrorMessage
            isValidation
            message={
              errors.ticketAlphaNumId.message ||
              'Please enter a valid reference ID'
            }
          />
        )}
      </div>

      <div className="flex justify-end gap-2 mt-2">
        <Button type="button" variant="secondary" onClick={closeForm}>
          {submitToSDK ? 'Back' : 'Close'}
        </Button>
        <Button
          type="button"
          onClick={handleSubmit(onFormSubmit)}
          disabled={locationSelected?.id ? false : submitToSDK ? true : false}
        >
          {submitToSDK ? 'Next' : 'Save Payment Info'}
        </Button>
      </div>
    </form>
  );
};

export default PaymentInfoForm;
